import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useQuery, useMutation } from 'react-query';
import { View, Text } from 'react-native';
import { useToast } from 'react-native-toast-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ConfirmationPopup, {
  ConfirmationPopupRef,
} from '../layout/ConfirmationPopup';
import { getPendingPayments } from '../../hooks/usePayments';
import { Payment } from '../../dtos/payment';
import { QueryKey } from '../../services/api/query';
import { getPayments } from '../../services/paymentService';
import { ApiError } from '../../services/api/api';
import Snackbar from '../atoms/Snakbar';
import { getJob } from '../../services/jobService';
import {
  deleteUserConsent,
  getActiveConnections,
  generateNewConsentLink,
} from '../../services/bankService';
import { Job, JobStatus } from '../../dtos/job';
import { User } from '../../dtos/user';
import { getUser } from '../../services/userService';
import { useUser } from '../../context/userContext';

import { DASHBOARD_GRANTED_TIME, IJobFeedback } from '../../constants/Common';

const DeleteConsentSteps: IJobFeedback = {
  steps: [
    {
      title: 'Deleting bank connection',
      context: {
        status: 'waiting',
      },
    },
    {
      title: 'Verifying deletion',
      context: {
        status: 'waiting',
      },
    },
  ],
  description:
    'We are un-linking your financial institution. This could take a minute.',
};

const ActivateConsentSteps: IJobFeedback = {
  steps: [
    {
      title: 'Creating new consent link',
      context: {
        status: 'waiting',
      },
    },
  ],
  description:
    'We are linking your financial institution. This could take a minute.',
};

const ConsentUpdate: React.ForwardRefRenderFunction<
  ConfirmationPopupRef,
  {}
> = ({}, ref) => {
  const toast = useToast();
  const { user } = useUser();
  const ConfirmRefModal = useRef<ConfirmationPopupRef>(null);

  const [pendingPaymentCount, setPendingPaymentCount] = useState<number>();
  const [userConsentURL, setUserConsentURL] = useState<string>('');

  const [isConsentActivated, setIsConsentActivated] = useState<boolean>(true);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [isDeletingConsent, setIsDeletingConsent] = useState<boolean>(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [activeExecutionCounter, setActiveExecutionCounter] = useState(0);

  const [loaderSteps, setLoaderSteps]: any = useState<IJobFeedback | null>(
    null,
  );

  useImperativeHandle(ref, () => ({
    show: () => {
      ConfirmRefModal.current?.show();
      setIsDataLoading(true);
      setShowSuccessDialog(false);
      if (user?.isDeactivated) {
        const tempSteps: any = { ...ActivateConsentSteps };
        tempSteps.steps[0].context.status = 'pending';
        setLoaderSteps(tempSteps);
      } else {
        const tempSteps: any = { ...DeleteConsentSteps };
        tempSteps.steps[0].context.status = 'pending';
        setLoaderSteps(tempSteps);
      }
    },
    hide: () => {
      handleConsentCancel();
    },
  }));

  const isJobStatusFailed =
    (user?.basiqJobStatus &&
      user?.basiqJobStatus?.length > 0 &&
      user?.basiqJobStatus.filter((job) => job.status === 'failed').length >
        0) ||
    false;

  useEffect(() => {
    if (user?.isDeactivated) {
      setIsConsentActivated(false);
      setUserConsentURL(user?.consent);
    } else {
      setIsConsentActivated(true);
    }
  }, [user]);

  const handleConsentCancel = () => {
    setShowSuccessDialog(false);
    setPendingPaymentCount(0);
    setIsDataLoading(false);
    setIsDeletingConsent(false);
    setLoaderSteps(null);
  };

  // Consent deactivation request
  const { mutate: handleDeleteConsent, data: DeleteConsentStatus } =
    useMutation(deleteUserConsent, {
      onError: () => {
        toast.show('Something went wrong. Please try again later', {
          type: 'danger',
        });
        ConfirmRefModal.current?.hide();
        setIsDeletingConsent(false);
      },
    });

  // Gettings pending payments count
  const {} = useQuery<Payment[] | null, ApiError>({
    queryKey: [QueryKey.Payments],
    queryFn: () => getPayments(),
    enabled: isConsentActivated && isDataLoading,
    onSuccess: (data: any) => {
      const payments: any = getPendingPayments(data);
      setPendingPaymentCount(payments.length || 0);
      if (isConsentActivated) {
        setIsDataLoading(false);
      }
    },
    onError: (error: any) => {
      toast.show('Something went wrong', {
        type: 'danger',
      });
      setIsDataLoading(false);
    },
  });

  const { data: BankConnections } = useQuery({
    queryKey: [QueryKey.BankConnections],
    queryFn: () => getActiveConnections(),
    enabled: !isConsentActivated && isDataLoading,
    onSuccess: (data) => {
      if (data.length && !isJobStatusFailed) {
        setIsDataLoading(false);
      } else {
        setActiveExecutionCounter(1);
      }
    },
    onError: () => {
      toast.show('Something went wrong', {
        type: 'danger',
      });
      setIsDataLoading(false);
    },
  });

  // Generate new consent link request
  const { data: ConsentLinkStatus } = useQuery({
    enabled:
      (BankConnections?.length === 0 || isJobStatusFailed) &&
      activeExecutionCounter === 1,
    queryFn: () => generateNewConsentLink(),
    queryKey: [QueryKey.GenerateNewConsent],
    onError: () => {
      toast.show('Something went wrong. Please try again later', {
        type: 'danger',
      });
      ConfirmRefModal.current?.hide();
    },
    onSuccess: () => {
      setActiveExecutionCounter(2);
    },
  });

  const { data: JobData } = useQuery<Job | null, ApiError>({
    queryKey: [QueryKey.Jobs],
    queryFn: () => {
      const jobId = isConsentActivated
        ? DeleteConsentStatus?._id
        : ConsentLinkStatus?._id;
      return getJob(jobId!); // Using ! since enabled condition ensures jobId exists
    },
    enabled: !!(isConsentActivated
      ? DeleteConsentStatus?._id
      : ConsentLinkStatus?._id),
    refetchInterval: (data) => {
      return data && data?.status === JobStatus.Pending ? 1000 : false;
    },
    onSuccess: (data: any) => {
      if (data.status === JobStatus.Success) {
        if (isConsentActivated) {
          const tempSteps: any = { ...DeleteConsentSteps };
          tempSteps.steps[0].context.status = 'success';
          tempSteps.steps[1].context.status = 'pending';
          setLoaderSteps(tempSteps);
        } else {
          setActiveExecutionCounter(3);
        }
      }
      if (data.status === JobStatus.Fail) {
        toast.show('Something went wrong. Please try again', {
          type: 'danger',
        });

        ConfirmRefModal.current?.hide();
      }
    },
  });

  const {} = useQuery<User | null, ApiError>({
    queryKey: [QueryKey.User],
    queryFn: () => getUser(),
    enabled:
      JobData?.status === JobStatus.Success || activeExecutionCounter === 3,
    onSuccess: async (data: any) => {
      if (data?.externalUserId && data.consent) {
        setUserConsentURL(data.consent);
      }
      if (isConsentActivated) {
        try {
          await AsyncStorage.setItem(
            DASHBOARD_GRANTED_TIME,
            `${new Date().toISOString()}`,
          );
        } catch (e) {
          // saving error
        }
        if (loaderSteps.length > 1) {
          loaderSteps.steps[1].context.status = JobStatus.Success;
        }
        setIsDeletingConsent(false);
        setShowSuccessDialog(true);
      } else {
        loaderSteps.steps[0].context.status = JobStatus.Success;
        setIsDataLoading(false);
        setActiveExecutionCounter(0);
      }
      setLoaderSteps([...loaderSteps]);
    },
    onError: () => {
      toast.show('Something went wrong', {
        type: 'danger',
      });
      setIsDataLoading(false);
    },
  });

  const handleConsentUpdate = async () => {
    if (isConsentActivated) {
      setIsDeletingConsent(true);
      handleDeleteConsent();
    } else if (!showSuccessDialog && !isConsentActivated) {
      await AsyncStorage.removeItem(DASHBOARD_GRANTED_TIME);
      window.open(userConsentURL, '_self');
    }
    if (showSuccessDialog) {
      handleConsentCancel();
      ConfirmRefModal.current?.hide();
    }
  };

  return (
    <ConfirmationPopup
      ref={ConfirmRefModal}
      title={
        !showSuccessDialog
          ? `${isConsentActivated ? 'Un-Link ' : 'Link '}` +
            'Financial Institution'
          : 'Financial Institution Un-Linked'
      }
      sequentialLoader={loaderSteps}
      message={
        <View>
          {pendingPaymentCount && isConsentActivated && !showSuccessDialog ? (
            <Snackbar
              message={
                <Text>
                  Warning! You have {pendingPaymentCount} pending payments.
                  <br />
                  Consider them before deactivating consent.
                </Text>
              }
              isClosable={false}
              type="warning"
              containerStyle={{
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 8,
                paddingBottom: 8,
                textAlign: 'left',
              }}
            />
          ) : null}

          <Text style={{ paddingTop: '6px' }}>
            {!showSuccessDialog
              ? `Are you sure you want to ${
                  isConsentActivated ? 'un-link your' : 'link your'
                } financial institution?`
              : 'Your financial institution has been un-linked successfully'}
          </Text>
        </View>
      }
      onCancel={handleConsentCancel}
      onConfirm={handleConsentUpdate}
      confirmButtonType={
        !showSuccessDialog
          ? isConsentActivated
            ? 'alert'
            : 'success'
          : 'success'
      }
      confirmButtonText={
        !showSuccessDialog ? (isConsentActivated ? 'Un-Link' : 'Link') : 'Okay'
      }
      isLoading={isDataLoading || isDeletingConsent}
      isDefaultLoader={isDataLoading && isConsentActivated}
      hideCancelButton={!!showSuccessDialog}
    />
  );
};

export default forwardRef(ConsentUpdate);
